<template>
  <v-combobox ref="field" variant="outlined" :model-value="chips" :label="label" :prepend-inner-icon="icon" multiple chips closable-chips
    hide-no-data placeholder="Type an email and press Enter" :hint="hint" persistent-hint
    :counter="counter" :persistent-counter="Boolean(counter)" :error-messages="errorMessages" :rules="rules" validate-on="blur" hide-details="auto"
    @update:model-value="onChange">
    <template #chip="{ props: chipProps, internalItem: item }">
      <v-chip v-bind="chipProps" size="small" :color="isInvalid(item.raw) ? 'error' : 'brand-color'"
        variant="tonal" label
        :prepend-icon="isInvalid(item.raw) ? 'mdi-alert-decagram' : 'mdi-email-outline'" />
    </template>
  </v-combobox>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { generateRules } from '@/plugins/validation/handler'
import { disposableEmailRule, findDisposableEmails } from '@/utils/disposableEmail'

const EMAIL_SEPARATORS = /[\s,;]+/
const INVALID_EMAIL_MESSAGE = 'Addresses shown in red do not look like valid emails.'
const emailRule = generateRules([{ rule: 'commaSeparatedEmails' }])[0]

const props = defineProps({
  // Comma-separated addresses, the format the API stores
  modelValue: { type: String, default: '' },
  label: { type: String, required: true },
  icon: { type: String, default: undefined },
  hint: { type: String, default: 'Press Enter after each address, or paste several at once.' },
  counter: { type: Number, default: undefined },
  required: { type: Boolean, default: false },
  errorMessages: { type: Array, default: () => [] }
})
const emit = defineEmits(['update:modelValue'])
const field = ref(null)

// Lets a parent run the rules on demand, e.g. before sending
defineExpose({ validate: () => field.value.validate() })

const chips = computed(() => (props.modelValue || '').split(EMAIL_SEPARATORS).filter(Boolean))

const rules = computed(() => [
  ...generateRules([
    ...(props.required ? [{ rule: 'required', msg: `${props.label} is required` }] : []),
    { rule: 'commaSeparatedEmails', msg: INVALID_EMAIL_MESSAGE }
  ]),
  disposableEmailRule
])

// Addresses on a disposable domain, looked up whenever the list changes (also covers saved values)
const disposableChips = ref([])
watch(chips, async (list) => {
  disposableChips.value = await findDisposableEmails(list)
}, { immediate: true })

function isInvalid(email) {
  const value = String(email).trim()
  return emailRule(value) !== true || disposableChips.value.includes(value.toLowerCase())
}

// A pasted list arrives as one chip: split it and drop duplicates.
function onChange(values) {
  const emails = new Set()
  values.forEach((value) => {
    String(value).split(EMAIL_SEPARATORS).filter(Boolean).forEach((email) => emails.add(email))
  })
  emit('update:modelValue', [...emails].join(','))
}
</script>
